<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import AUApi from "@/api";
import { getKennungValueDisplay, getKennungValue } from "@/DataHelper";
import { Globals } from "@/Globals";
import { globalsBezeichnung, checkBegriff } from "@/local";
import { getSiteData, goBack } from "@/NavigationHelper";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
let disabled = ref<boolean>(false);
let deleteVisible = ref<boolean>(true);
let saveVisible = ref<boolean>(true);
let backVisible = ref<boolean>(false);
let showZeitzone = ref<boolean>(false);
let showbde = ref<boolean>(false);
let editAntragReisen = ref<boolean>(false);
let datenloeschen = ref<boolean>(true);
const siteData = getSiteData();
const emit = defineEmits<{
  back: [];
  save: [];
  calculated: [data?: any];
}>();
const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_editStempelantrag",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  const zeitzone = checkBegriff("PZE_Web_Zeitzone_Nacherfassen");
  if (zeitzone) {
    showZeitzone.value = true;
  }
  if (siteData) {
    if (siteData?.freigabe) {
      disabled.value = true;
      deleteVisible.value = false;
      pageParams.query = "Web_Freigabe_Stempelantrag";
    }
    if (siteData?.bde) {
      showbde.value = true;
    }
    if (siteData?.oriAIC === 0) {
      datenloeschen.value = false;
    }
    if (siteData?.reise) {
      editAntragReisen.value = true;
    }
    if (siteData?.noDelete) {
      deleteVisible.value = false;
    }
  }
  await mountedBase(uiData, pageParams, false);
  if (uiData.data[0]) {
    const antrag = getKennungValue(uiData.data[0], "ZE_ANTRAG_BOOL3", uiData.columns);
    if ((antrag === 1 && !siteData?.freigabe) || !uiData.header.edit) {
      disabled.value = true;
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
function save() {
  onSave(uiData, pageParams)?.then(() => {
    emit("back");
  });
}

async function onDeleteAntrag() {
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    await onDelete(uiData, pageParams, uiData.data[0].aic_Bew_pool, "");
    emit("back");
  }
}
</script>
<template>
  <v-card v-if="uiData.initialized" :loading="uiData.showLoading">
    <v-card-title v-if="!siteData?.freigabe">
      {{ globalsBezeichnung(Globals.Begriff, "Web_original", "Original") }}
      {{ getKennungValueDisplay(uiData.data[0], "ZE_UHRZEIT_ALT", uiData.columns) }}
    </v-card-title>
    <v-card-text class="pt-2">
      <v-row v-if="!siteData?.freigabe">
        <v-col cols="12" xs="6" sm="6" class="py-0">
          <EditTimePicker
            :disabled="disabled"
            kennung="ZE_UHRZEIT_NEU"
            formatProp="HH:mm"
          />
        </v-col>
        <v-col cols="12" xs="6" sm="6" class="py-0">
          <EditComboBox v-if="showZeitzone" kennung="BEW_ZEITZONE" />
        </v-col>
        <v-row v-if="!siteData?.freigabe && !editAntragReisen && datenloeschen">
          <v-col cols="12" xs="6" sm="6" class="py-0">
            <EditCheckBox
              class="ml-2 mb-5"
              :disabled="disabled"
              kennung="ZE_DATENSATZ_LOESCHE"
            />
          </v-col>
        </v-row>
        <v-row v-if="editAntragReisen && !siteData?.freigabe && !datenloeschen">
          <v-col cols="12" xs="6" sm="6" class="py-0">
            <EditCheckBox
              class="ml-2 mb-5"
              :disabled="true"
              kennung="ZE_DATENSATZ_LOESCHE"
            />
          </v-col>
        </v-row>
      </v-row>
      <v-row v-if="siteData?.freigabe" class="pt-2 pl-3">
        <v-col cols="12" xs="6" sm="6" class="pa-0">
          <div class="pt-0 pt-sm-2">
            {{ globalsBezeichnung(Globals.Begriff, "Web_original", "Original") }}
            {{ getKennungValueDisplay(uiData.data[0], "ZE_UHRZEIT_ALT", uiData.columns) }}
          </div>
        </v-col>
        <v-col cols="12" xs="6" sm="6" class="pa-0">
          <EditCheckBox :disabled="disabled" kennung="ZE_DATENSATZ_LOESCHE" />
        </v-col>
      </v-row>
      <v-row v-if="siteData?.freigabe" class="pl-3 mt-0 pa-0 mb-3">
        <div>
          {{ globalsBezeichnung(Globals.Begriff, "neu", "neu") }}
          {{ getKennungValueDisplay(uiData.data[0], "ZE_UHRZEIT_NEU", uiData.columns) }}
        </div>
      </v-row>

      <EditComboBox
        v-if="!showbde"
        :disabled="disabled"
        :clearable="true"
        kennung="PHYS_STEMPELUNG"
      />

      <EditComboBox
        v-if="editAntragReisen"
        :disabled="disabled"
        kennung="BEWZEITART"
        :clearable="true"
        :readonly="true"
      />

      <EditComboBox
        v-if="!editAntragReisen"
        :disabled="true"
        kennung="BEWZEITART"
        :clearable="true"
      />
      <EditComboBox
        v-if="showbde"
        :disabled="disabled"
        kennung="INSTRUCTION"
        :clearable="true"
      />
      <EditComboBox
        v-if="showbde"
        :disabled="disabled"
        kennung="ACTIVITY"
        :clearable="true"
      />

      <EditComboBox
        v-if="showbde"
        :disabled="disabled"
        kennung="COST CENTRE"
        :clearable="true"
      />

      <EditTextArea :disabled="disabled" kennung="MEMO" :clearable="false" />

      <EditTextArea :disabled="disabled" kennung="NOTIZ" :clearable="false" />

      <v-row>
        <EditCheckBox3 v-if="siteData?.freigabe" kennung="ZE_ANTRAG_BOOL3" />
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="deleteVisible"
        :backVisible="backVisible"
        :createVisible="false"
        :refreshVisible="false"
        @delete="onDeleteAntrag"
        :saveVisible="saveVisible"
        @back="goBack()"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
